import classNames from 'classnames';
import queryString from 'query-string';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSearch } from 'wouter';

import useFiltersQuery from '../../hooks/useFiltersQuery';
import useScrollEnter from '../../hooks/useScrollEnter';
import { useSubscription } from '../../hooks/useSubscription';
import { usePageViewTracking } from '../../hooks/useTracking';
import { isMessage } from '../../lib/utils';

import { useBrand, useDefaultBrand } from '../../contexts/SiteContext';
import { typesSections } from '../../routes';
import HeaderAd from '../ads/HeaderAd';
import { CollectionsRow } from '../blocks';
import SubscriptionForm from '../forms/SubscriptionForm';
import IndexDefaultHeader from '../headers/IndexDefaultHeader';
import InfiniteItemsBlocks from '../lists/InfiniteItemsBlocks';
import TypesMenu from '../menus/TypesMenu';

import styles from '../../styles/pages/all-page.module.css';

export interface AllPageProps {
    brand?: string | null;
    type?: string;
    sponsorised?: boolean;
    current?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: (() => void) | null;
    onLeave?: (() => void) | null;
}

const AllPage = ({
    brand: brandSlug = null,
    type: typeSlug = null,
    sponsorised = false,
    current = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: AllPageProps) => {
    const intl = useIntl();
    const type = typesSections.find(({ id, path }) => path === typeSlug || id === typeSlug) || null;
    const { id: typeId, query, title, indexTitle = null } = type || {};
    const filters = useFiltersQuery();
    const hasBrand = brandSlug !== null;
    const { handle: defaultBrandHandle = 'urbania' } = useDefaultBrand();
    const brand = useBrand(brandSlug, !hasBrand ? defaultBrandHandle : null);
    const { handle: brandHandle = null, label: brandLabel } = brand || {};
    const { subscribed = false } = useSubscription({
        subscription: brandHandle,
        source: 'archives',
    });

    const search = useSearch();
    const { sort: finalSort = null } = queryString.parse(search);

    useScrollEnter({
        disabled: !current,
        onEnter,
        onLeave,
    });

    usePageViewTracking(current, {
        brand,
        pageType: 'index',
    });

    const isVideosTheme = typeId === 'videos' || typeId === 'podcasts' || typeId === 'micromags';
    const isPodcast = typeId === 'podcasts';
    const isMicromag = typeId === 'micromags';

    let listProps = null;
    if (isPodcast) {
        listProps = {
            cardType: 'horizontal',
            cardTheme: 'box',
            cardRatio: 'square',
            blockProps: {
                autoColumns: true,
                cardWidth: 280,
            },
        };
    } else if (isVideosTheme) {
        listProps = {
            cardTheme: 'rounded',
            cardRatio: isMicromag ? 'micromag' : null,
        };
    } else if (typeSlug === 'popular') {
        listProps = {
            hook: 'popular_documents',
        };
    }

    return (
        <div
            className={classNames([styles.container, className, contentClassName])}
            data-brand={brandHandle}
            // data-theme={!sponsorised ? typeId : null}
        >
            <HeaderAd />
            <IndexDefaultHeader
                title={
                    <>
                        {typeId === 'popular' ? (
                            <FormattedMessage
                                defaultMessage="Les plus populaires"
                                description="Page title"
                            />
                        ) : (
                            indexTitle || (isMessage(title) ? intl.formatMessage(title) : title)
                        )}
                        {sponsorised ? (
                            <small>
                                <FormattedMessage
                                    defaultMessage="Sponso"
                                    description="Page title"
                                />
                            </small>
                        ) : null}
                    </>
                }
                className={styles.header}
            />
            <TypesMenu
                sort={finalSort}
                brand={brand}
                sponsorised={sponsorised}
                className={styles.filters}
            />
            {typeId === 'newsletters' && !subscribed ? (
                <div className={styles.subscriptionSection}>
                    <h1 className={styles.title}>
                        <FormattedMessage
                            defaultMessage="Abonnez-vous à {brand}!"
                            description="Page title"
                            values={{
                                brand:
                                    brandHandle === 'france' ? 'la newsletter URBANIA' : brandLabel,
                            }}
                        />
                    </h1>
                    <SubscriptionForm
                        subscription={brandHandle}
                        className={styles.subscriptionForm}
                        ssoClassName={styles.sso}
                    />
                </div>
            ) : null}
            <main className={styles.content}>
                {typeId === 'podcasts' ? (
                    <CollectionsRow
                        query={{
                            type: 'serie_podcast',
                            placement: [
                                'podcasts_collections_urbania',
                                'podcasts_collections_quatre95',
                                'podcasts_collections_mollo',
                            ],
                            brand: brandHandle !== 'urbania' ? brand.id : null,
                            ...(sponsorised ? { sponsorised: true } : null),
                        }}
                        cardTheme="rounded"
                        cardRatio="square"
                        cardSize="big"
                        cardWith={['brand-icon']}
                        count={12}
                        className={styles.series}
                    />
                ) : null}
                <InfiniteItemsBlocks
                    query={{
                        ...query,
                        brand: brandHandle !== 'urbania' ? brand.id : null,
                        ...(sponsorised ? { sponsorised: true } : null),
                        ...filters,
                    }}
                    cardSize="medium"
                    cardWith={
                        (brandHandle === 'urbania' && typeId !== 'micromags') || sponsorised
                            ? ['brand-icon']
                            : null
                    }
                    cardWithout={[
                        typeId === 'micromags' ? 'time' : null,
                        isVideosTheme ? 'author' : null,
                    ].filter((it) => it !== null)}
                    {...listProps}
                />
            </main>
        </div>
    );
};

export default AllPage;
